import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LocalizedDateInput, Spinner } from '../../../shared/components';
import { addDays, parseLocalDateKey, toDateKey } from '../../../shared/utils/calendarPickerUtils';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import catalog from '../../catalog/Catalog.module.css';
import { listLocations } from '../../companies';
import { Location } from '../../companies/types/Company';
import { FALLBACK_CURRENCY } from '../invoiceForm';
import { getInvoices, getInvoiceSettings, getInvoiceSummary } from '../services/invoiceService';
import { rememberTenantCurrency } from '../tenantCurrency';
import { InvoiceDaySummary, InvoiceDocumentType, InvoiceListItem, InvoiceStatus } from '../types/Invoice';
import { InvoiceCashDayPanel } from './InvoiceCashDayPanel';
import { InvoiceDaySummaryPanel } from './InvoiceDaySummaryPanel';
import { InvoiceListTable } from './InvoiceListTable';
import styles from '../Invoice.module.css';

const ALL_PAGE_SIZE = 25;
const DAY_PAGE_SIZE = 50;

const todayKey = () => toDateKey(new Date());

const shiftDateKey = (value: string, days: number) => {
  const parsed = parseLocalDateKey(value);
  return parsed ? toDateKey(addDays(parsed, days)) : value;
};

const emptyPaged = { items: [] as InvoiceListItem[], page: 1, pageSize: DAY_PAGE_SIZE, total: 0, hasNext: false };

export const Invoices: React.FC = () => {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission(PermissionScopes.invoicesCreate);
  const canReadLocations = hasPermission(PermissionScopes.companiesRead);

  const viewParam = searchParams.get('view');
  const date = searchParams.get('date') || todayKey();
  const searchTerm = searchParams.get('q') ?? '';
  const status = searchParams.get('status') ?? '';
  const documentType = searchParams.get('documentType') ?? '';
  const overdueOnly = searchParams.get('overdue') === '1';
  const rangeFrom = searchParams.get('from') ?? '';
  const rangeTo = searchParams.get('to') ?? '';
  const locationId = searchParams.get('locationId') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') || '1') || 1);

  const [searchDraft, setSearchDraft] = useState(searchTerm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [summary, setSummary] = useState<InvoiceDaySummary | null>(null);
  const [issued, setIssued] = useState(emptyPaged);
  const [collected, setCollected] = useState(emptyPaged);
  const [drafts, setDrafts] = useState(emptyPaged);
  const [ledger, setLedger] = useState(emptyPaged);
  const [useDayView, setUseDayView] = useState(false);
  const [tenantCurrency, setTenantCurrency] = useState(FALLBACK_CURRENCY);
  const [preferenceReady, setPreferenceReady] = useState(viewParam === 'all' || viewParam === 'day');
  const [dayTab, setDayTab] = useState<'issued' | 'collected' | 'drafts'>('issued');

  const view = viewParam === 'all' ? 'all' : viewParam === 'day' ? 'day' : (useDayView ? 'day' : 'all');

  const today = todayKey();
  const isFutureDate = date > today;

  const patchParams = (patch: Record<string, string | null>, replace = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace });
  };

  useEffect(() => {
    setSearchDraft(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;
    getInvoiceSettings()
      .then(data => {
        rememberTenantCurrency(data.defaultCurrency);
        if (!cancelled) {
          setUseDayView(Boolean(data.useDayView));
          setTenantCurrency(data.defaultCurrency);
        }
      })
      .catch(() => {
        if (!cancelled) setUseDayView(false);
      })
      .finally(() => {
        if (!cancelled) setPreferenceReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canReadLocations) {
      setLocations([]);
      return;
    }

    let cancelled = false;
    listLocations({ isActive: true, pageSize: 200 })
      .then(data => {
        if (!cancelled) setLocations(data);
      })
      .catch(() => {
        if (!cancelled) setLocations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canReadLocations]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchDraft.trim() === searchTerm) return;
      setSearchParams(previous => {
        const next = new URLSearchParams(previous);
        const value = searchDraft.trim();
        if (value) next.set('q', value);
        else next.delete('q');
        next.delete('page');
        return next;
      }, { replace: true });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchDraft, searchTerm, setSearchParams]);

  useEffect(() => {
    setDayTab('issued');
  }, [date, locationId]);

  useEffect(() => {
    if (view !== 'day' || !isFutureDate) return;
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('date', today);
      return next;
    }, { replace: true });
  }, [isFutureDate, setSearchParams, today, view]);

  useEffect(() => {
    if (!preferenceReady) return;
    const load = async () => {
      setLoading(true);
      try {
        const shared = {
          q: searchTerm.trim() || undefined,
          documentType: documentType ? documentType as InvoiceDocumentType : undefined,
          locationId: locationId || undefined,
        };

        if (view === 'day') {
          const day = isFutureDate ? today : date;
          const [summaryData, issuedData, collectedData, draftData] = await Promise.all([
            getInvoiceSummary(day, day, locationId || undefined),
            getInvoices({
              ...shared,
              from: day,
              to: day,
              status: status ? status as InvoiceStatus : undefined,
              overdue: overdueOnly || undefined,
              page: 1,
              pageSize: DAY_PAGE_SIZE,
            }),
            getInvoices({
              ...shared,
              issuedBefore: day,
              paidFrom: day,
              paidTo: day,
              page: 1,
              pageSize: DAY_PAGE_SIZE,
            }),
            getInvoices({
              ...shared,
              status: 'draft',
              from: day,
              to: day,
              page: 1,
              pageSize: DAY_PAGE_SIZE,
            }),
          ]);
          setSummary(summaryData);
          setIssued(issuedData);
          setCollected(collectedData);
          setDrafts(draftData);
          setError(null);
          return;
        }

        const ledgerData = await getInvoices({
          ...shared,
          status: status ? status as InvoiceStatus : undefined,
          overdue: overdueOnly || undefined,
          from: rangeFrom || undefined,
          to: rangeTo || undefined,
          page,
          pageSize: ALL_PAGE_SIZE,
        });
        setLedger(ledgerData);
        setSummary(null);
        setError(null);
      } catch {
        setError(t('failedToLoad', { ns: 'invoices' }));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [date, documentType, isFutureDate, locationId, overdueOnly, page, preferenceReady, rangeFrom, rangeTo, searchTerm, status, t, today, view]);

  const showLocation = locations.length > 0
    || issued.items.some(item => item.locationName)
    || collected.items.some(item => item.locationName)
    || drafts.items.some(item => item.locationName)
    || ledger.items.some(item => item.locationName);
  const openInvoice = (item: InvoiceListItem) => navigate(`/invoices/${item.id}`);
  const currency = summary?.currency ?? tenantCurrency;

  return (
    <div className={`${catalog.page} ${styles.page}`}>
      <div className={catalog.headerRow}>
        <h2 className={catalog.title}>{t('title', { ns: 'invoices' })}</h2>
        <div className={catalog.headerActions}>
          <button className="btn btn-secondary" type="button" onClick={() => navigate('/invoices/settings')}>
            {t('settings', { ns: 'invoices' })}
          </button>
          {canCreate ? (
            <button className="btn btn-primary" type="button" onClick={() => navigate('/invoices/add')}>
              {t('addInvoice', { ns: 'invoices' })}
            </button>
          ) : null}
        </div>
      </div>

      <div className={catalog.toolbar}>
        <div className={styles.viewToggle} role="group" aria-label={t('viewLabel', { ns: 'invoices' })}>
          <button
            type="button"
            className={`${styles.viewToggleButton} ${view === 'day' ? styles.viewToggleButtonActive : ''}`}
            aria-pressed={view === 'day'}
            onClick={() => {
              const next: Record<string, string | null> = { view: 'day', page: null, from: null, to: null, date: date || today };
              if (status === 'draft') next.status = null;
              patchParams(next, false);
            }}
          >
            {t('viewDay', { ns: 'invoices' })}
          </button>
          <button
            type="button"
            className={`${styles.viewToggleButton} ${view === 'all' ? styles.viewToggleButtonActive : ''}`}
            aria-pressed={view === 'all'}
            onClick={() => patchParams({ view: 'all', date: null, page: null }, false)}
          >
            {t('viewAll', { ns: 'invoices' })}
          </button>
        </div>
        {view === 'day' ? (
          <div className={styles.dayNav}>
            <button
              className={styles.dayNavButton}
              type="button"
              aria-label={t('previousDay', { ns: 'invoices' })}
              onClick={() => patchParams({ date: shiftDateKey(date, -1) })}
            >
              ‹
            </button>
            <LocalizedDateInput
              className={styles.toolbarControl}
              value={date}
              displayFormat="long"
              onValueChange={value => patchParams({ date: value && value > today ? today : value })}
              emptyLabel={t('viewDay', { ns: 'invoices' })}
            />
            <button
              className={styles.dayNavButton}
              type="button"
              aria-label={t('nextDay', { ns: 'invoices' })}
              disabled={date >= today}
              onClick={() => patchParams({ date: shiftDateKey(date, 1) })}
            >
              ›
            </button>
            {date !== today ? (
              <button className={styles.dayNavToday} type="button" onClick={() => patchParams({ date: today })}>
                {t('today', { ns: 'invoices' })}
              </button>
            ) : null}
          </div>
        ) : null}
        <div className={catalog.searchWrap}>
          <input
            className={catalog.searchInput}
            type="text"
            placeholder={t('searchPlaceholder', { ns: 'invoices' })}
            value={searchDraft}
            onChange={event => setSearchDraft(event.target.value)}
          />
        </div>
        <select
          className={catalog.filterSelect}
          value={documentType}
          onChange={event => patchParams({ documentType: event.target.value || null, page: null })}
        >
          <option value="">{t('allTypes', { ns: 'invoices' })}</option>
          <option value="invoice">{t('typeInvoice', { ns: 'invoices' })}</option>
          <option value="creditNote">{t('typeCreditNote', { ns: 'invoices' })}</option>
        </select>
        {locations.length > 0 ? (
          <select
            className={catalog.filterSelect}
            value={locationId}
            onChange={event => patchParams({ locationId: event.target.value || null, page: null })}
            aria-label={t('location', { ns: 'invoices' })}
          >
            <option value="">{t('allLocations', { ns: 'invoices' })}</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
        ) : null}
        <select
          className={catalog.filterSelect}
          value={status}
          onChange={event => patchParams({ status: event.target.value || null, page: null })}
        >
          <option value="">{t('allStatuses', { ns: 'invoices' })}</option>
          {view === 'all' ? <option value="draft">{t('statusDraft', { ns: 'invoices' })}</option> : null}
          <option value="issued">{t('statusIssued', { ns: 'invoices' })}</option>
          <option value="partiallyPaid">{t('statusPartiallyPaid', { ns: 'invoices' })}</option>
          <option value="paid">{t('statusPaid', { ns: 'invoices' })}</option>
          <option value="void">{t('statusVoid', { ns: 'invoices' })}</option>
        </select>
        {view === 'all' ? (
          <>
            <LocalizedDateInput
              className={styles.toolbarControl}
              value={rangeFrom}
              onValueChange={value => patchParams({ from: value || null, page: null })}
              emptyLabel={t('fromDate', { ns: 'invoices' })}
            />
            <LocalizedDateInput
              className={styles.toolbarControl}
              value={rangeTo}
              onValueChange={value => patchParams({ to: value || null, page: null })}
              emptyLabel={t('toDate', { ns: 'invoices' })}
            />
          </>
        ) : null}
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={event => patchParams({ overdue: event.target.checked ? '1' : null, page: null })}
          />
          {t('overdueOnly', { ns: 'invoices' })}
        </label>
      </div>

      {error ? <div className="error">{error}</div> : null}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
          <Spinner text={t('loading', { ns: 'invoices' })} />
        </div>
      ) : view === 'day' ? (
        <div className={styles.dayView}>
          {summary ? <InvoiceDaySummaryPanel summary={summary} currency={currency} /> : null}
          {useDayView ? (
            <InvoiceCashDayPanel date={isFutureDate ? today : date} locationId={locationId || undefined} currency={currency} />
          ) : null}
          <section className={styles.dayLists}>
            <div className={styles.dayTabs} role="tablist" aria-label={t('viewDay', { ns: 'invoices' })}>
              <button
                type="button"
                role="tab"
                aria-selected={dayTab === 'issued'}
                className={dayTab === 'issued' ? styles.dayTabActive : styles.dayTab}
                onClick={() => setDayTab('issued')}
              >
                {t('tabIssued', { ns: 'invoices' })}
                <span className={styles.dayTabCount}>{issued.total || issued.items.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={dayTab === 'collected'}
                className={dayTab === 'collected' ? styles.dayTabActive : styles.dayTab}
                onClick={() => setDayTab('collected')}
              >
                {t('tabCollected', { ns: 'invoices' })}
                <span className={styles.dayTabCount}>{collected.total || collected.items.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={dayTab === 'drafts'}
                className={dayTab === 'drafts' ? styles.dayTabActive : styles.dayTab}
                onClick={() => setDayTab('drafts')}
              >
                {t('tabDrafts', { ns: 'invoices' })}
                <span className={styles.dayTabCount}>{drafts.total || drafts.items.length}</span>
              </button>
            </div>
            {dayTab === 'issued' ? (
              <InvoiceListTable compact items={issued.items} emptyLabel={t('emptyIssuedDay', { ns: 'invoices' })} onOpen={openInvoice} showLocation={showLocation} />
            ) : null}
            {dayTab === 'collected' ? (
              <InvoiceListTable compact items={collected.items} emptyLabel={t('emptyCollectedDay', { ns: 'invoices' })} onOpen={openInvoice} showLocation={showLocation} />
            ) : null}
            {dayTab === 'drafts' ? (
              <InvoiceListTable compact items={drafts.items} emptyLabel={t('emptyDraftsDay', { ns: 'invoices' })} onOpen={openInvoice} showLocation={showLocation} />
            ) : null}
          </section>
        </div>
      ) : (
        <>
          <InvoiceListTable items={ledger.items} emptyLabel={t('empty', { ns: 'invoices' })} onOpen={openInvoice} showLocation={showLocation} />
          <div className={styles.pagination}>
            <button className="btn btn-secondary" type="button" disabled={page <= 1} onClick={() => patchParams({ page: page <= 2 ? null : String(page - 1) })}>
              {t('previous', { ns: 'common' })}
            </button>
            <span className={styles.pageMeta}>{t('pageCount', { ns: 'invoices', page, total: ledger.total })}</span>
            <button className="btn btn-secondary" type="button" disabled={!ledger.hasNext} onClick={() => patchParams({ page: String(page + 1) })}>
              {t('next', { ns: 'common' })}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Invoices;
